import { API_BASE_URL } from '@synergy/config';
import { BonusSettlementStatus, BonusSettlementType } from '@synergy/models';
import { Button } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PaloOverflowAction, PaloOverflowMenu } from '@synergy/components-palo';
import { PoolConfirmationModal } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components';
import { ReactElement, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { AddSupplierModal } from '../supplier-overview/components/bonus-settlement-supplier-overview-add-supplier.modal';
import { BonusSettlementPayoutModal } from '../../overview/components/bonus-settlement-payout-modal';
import { bonusSettlementsSupplierOverviewDetailsSelector } from '../supplier-overview/store/bonus-settlements-supplier-overview-selector';
import {
    completeSuppliers,
    fetchBonusSettlementsSupplierOverview,
    fetchBonusSettlementsSupplierOverviewDetails,
    inviteSupplier,
    remindSuppliers,
} from '../supplier-overview/store/bonus-settlements-supplier-overview-actions';
import { defaultSearchParamsSelector } from '../../../../store/default-searchparams-selector';
import { updateBonusSettlement } from '../../overview/store/bonus-settlements-overview-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface SupplierHeaderActionsProps {
    bonusSettlementId: number;
}

/**
 * What the supplier side of a settlement can do: one next step, the rest in
 * the overflow menu.
 *
 * Six buttons used to stand in the header at once, and the one that moved the
 * settlement forward looked like the five beside it (#2206).
 */
export const SupplierHeaderActions = (props: SupplierHeaderActionsProps): ReactElement | null => {
    const { bonusSettlementId } = props;
    const details = useAppSelector(bonusSettlementsSupplierOverviewDetailsSelector);
    const dispatch = useThunkDispatch();

    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams] = useSearchParams(defaultSearchParams);

    const [isOpenInviteSupplier, setIsOpenInviteSupplier] = useState<boolean>(false);
    const [isOpenCompleteAll, setIsOpenCompleteAll] = useState<boolean>(false);
    const [isOpenRemindAll, setIsOpenRemindAll] = useState<boolean>(false);
    const [isOpenAddSupplier, setIsOpenAddSupplier] = useState<boolean>(false);
    const [isOpenPayout, setIsOpenPayout] = useState<boolean>(false);

    // The quota used to be reachable only from the three-dot menu of the
    // overview — away from where one works on the settlement (#2211).
    const onSavePayout = async (payout: number) => {
        setIsOpenPayout(false);
        await dispatch(updateBonusSettlement({ id: bonusSettlementId, payout }));
        await dispatch(fetchBonusSettlementsSupplierOverviewDetails(bonusSettlementId));
    };

    const onInviteSuppliers = async () => {
        await dispatch(inviteSupplier(bonusSettlementId));
        await dispatch(fetchBonusSettlementsSupplierOverview({ id: +bonusSettlementId, searchParams: searchParams }));

        setIsOpenInviteSupplier(false);
    };

    const onRemindAllSuppliers = () => {
        if (bonusSettlementId && +bonusSettlementId) {
            dispatch(
                remindSuppliers({
                    bonusSettlementId,
                    remindAll: true,
                    supplierCount: details?.openCount,
                })
            );
        }
        setIsOpenRemindAll(false);
    };

    const onCompleteAllSuppliers = async () => {
        if (bonusSettlementId && +bonusSettlementId) {
            await dispatch(
                completeSuppliers({
                    bonusSettlementId: +bonusSettlementId,
                    completeAll: true,
                    bonusSettlementSupplierIds: undefined,
                    supplierName: undefined,
                    supplierCount: details?.sentCount,
                })
            );
            dispatch(
                fetchBonusSettlementsSupplierOverview({
                    id: +bonusSettlementId,
                    searchParams: searchParams,
                })
            );
            dispatch(fetchBonusSettlementsSupplierOverviewDetails(+bonusSettlementId));
        }
        setIsOpenCompleteAll(false);
    };

    if (!details) {
        return null;
    }

    const handleExport = () => {
        if (bonusSettlementId && +bonusSettlementId) {
            window.open(urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/export`), '_self');
        }
    };

    const isDraft = BonusSettlementStatus.Draft === details.status;
    const isSent = BonusSettlementStatus.Sent === details.status;

    const menuActions: PaloOverflowAction[] = [
        ...(isDraft || isSent
            ? [
                  {
                      label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.addSupplier" />,
                      onClick: () => setIsOpenAddSupplier(true),
                  },
              ]
            : []),
        ...([BonusSettlementStatus.Draft, BonusSettlementStatus.Sent, BonusSettlementStatus.ReadyToComplete].includes(
            details.status
        )
            ? [
                  {
                      label: <FormattedMessage id="app.bonusSettlements.payout" />,
                      onClick: () => setIsOpenPayout(true),
                  },
              ]
            : []),
        ...(isSent && BonusSettlementType.Main === details.type
            ? [
                  {
                      label: (
                          <FormattedMessage
                              id="app.bonusSettlementsSupplierOverview.action.remindAllSuppliers"
                              values={{ number: details.openCount }}
                          />
                      ),
                      onClick: () => setIsOpenRemindAll(true),
                  },
              ]
            : []),
        {
            label: <FormattedMessage id="app.bonusSettlementsSupplierOverview.evaluation" />,
            reason: <FormattedMessage id="app.bonusSettlementsSupplierOverview.evaluationTooltip" />,
            onClick: handleExport,
        },
    ];

    return (
        <>
            <PaloOverflowMenu actions={menuActions} />
            {isDraft && (
                <Button
                    key="sendToSupplier"
                    variant="contained"
                    color="primary"
                    size="small"
                    // Nothing to send to: the settlement has no supplier yet.
                    // The button used to offer it anyway (#2211).
                    disabled={0 === details.openCount}
                    onClick={() => setIsOpenInviteSupplier(true)}
                >
                    <FormattedMessage id="app.bonusSettlementsSupplierOverview.sendToSupplier" />
                </Button>
            )}
            {isSent && (
                <Button
                    key="completeAll"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => setIsOpenCompleteAll(true)}
                >
                    <FormattedMessage
                        id="app.bonusSettlementsSupplierOverview.action.completeAllWithNumber"
                        values={{ number: details.sentCount }}
                    />
                </Button>
            )}

            <PaloNextThemeProvider>
                <BonusSettlementPayoutModal
                    open={isOpenPayout}
                    payout={details.payout}
                    onClose={() => setIsOpenPayout(false)}
                    onSave={onSavePayout}
                />
                <PoolConfirmationModal
                    open={isOpenInviteSupplier}
                    isSuccessDialog={true}
                    title={<FormattedMessage id="app.bonusSettlementsSupplierOverview.sendToSupplier" />}
                    onCancel={() => setIsOpenInviteSupplier(false)}
                    onConfirm={onInviteSuppliers}
                    action={<FormattedMessage id="app.common.send" />}
                >
                    <PoolFormattedMessage
                        id="app.bonusSettlementsSupplierOverview.sendToSupplier.text"
                        values={{ year: details?.year, count: details?.openCount ?? 0 }}
                    />
                </PoolConfirmationModal>

                <PoolConfirmationModal
                    open={isOpenRemindAll}
                    isSuccessDialog={true}
                    title={
                        <FormattedMessage id="app.bonusSettlementsSupplierOverview.action.remindAllSuppliers.title" />
                    }
                    onCancel={() => setIsOpenRemindAll(false)}
                    onConfirm={onRemindAllSuppliers}
                    action={<FormattedMessage id="app.common.remind" />}
                >
                    <PoolFormattedMessage
                        id="app.bonusSettlementsSupplierOverview.action.remindAllSuppliers.text"
                        values={{ supplierCount: details?.openCount }}
                    />
                </PoolConfirmationModal>

                <PoolConfirmationModal
                    open={isOpenCompleteAll}
                    isSuccessDialog={true}
                    title={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.completeAll" />}
                    onCancel={() => setIsOpenCompleteAll(false)}
                    onConfirm={onCompleteAllSuppliers}
                    action={<FormattedMessage id="app.bonusSettlementsSupplierOverview.action.completeAll" />}
                >
                    <PoolFormattedMessage
                        id="app.bonusSettlementsSupplierOverview.action.completeAll.text"
                        values={{ number: details?.sentCount }}
                    />
                </PoolConfirmationModal>

                <AddSupplierModal
                    open={isOpenAddSupplier}
                    bonusSettlementId={bonusSettlementId ? +bonusSettlementId : 0}
                    onClose={() => setIsOpenAddSupplier(false)}
                />
            </PaloNextThemeProvider>
        </>
    );
};
